import { getVendorItemInfo } from "services/sosInventoryService/sosApi";
import { setOutboundBinsForLine } from "services/utility/bins";
import { getBaseUom } from "services/utility/uoms";

/**
 * @name    getVendorItemCatalogInfo
 *
 * @summary given a vendor and an item (and the default data, if no catalog
 *          entry), this function looks in the vendor-item catalog for vendor-
 *          specific pricing (and related vendor product number) and returns
 *          the values for vendor part number and item cost that should be
 *          shown on purchasing line items
 *
 * @param   vendor (standard reference object)
 *
 * @param   itemId (number) - item id
 *
 * @param   itemBasePurchaseCost (Money) - the purchase cost in the item record,
 *          which will be used if there is no entry for this vendor in the
 *          vendor-item catalog
 *
 * @param   itemVendorPartNumber (string) - the vendor part number from the
 *          item record, which will be used if there is no entry for this
 *          vendor in the vendor-item catalog
 *
 * @returns an object containing the vendor part number and the purchase cost
 *          that should be used on the line item
 */
export async function getVendorItemCatalogInfo(
  vendor,
  itemId,
  itemBasePurchaseCost,
  itemVendorPartNumber = ""
) {
  if (!vendor) {
    return {
      vendorPartNumber: itemVendorPartNumber,
      itemCost: itemBasePurchaseCost,
    };
  }

  // getVendorItemInfo returns an array; since we've only provided one
  // itemId, it will always be an array of 0 or 1 entries
  const [vendorItemInfo] = await getVendorItemInfo(vendor.id, itemId);
  const itemCost = vendorItemInfo?.price || itemBasePurchaseCost;
  const vendorPartNumber =
    vendorItemInfo?.vendorPartNumber || itemVendorPartNumber;

  return { vendorPartNumber, itemCost };
}

export async function updateLineWithItem(item, line, vendor) {
  if (!item) {
    return line;
  }

  const { vendorPartNumber, itemCost } = await getVendorItemCatalogInfo(
    vendor,
    item.id,
    item.basePurchaseCost,
    item.vendorPartNumber
  );

  // transfer the appropriate properties from the new inventory
  // item to the line item
  const newLine = {
    ...line,
    item,
    lot: null,
    bin: item.bin,
    description: descriptionForPurchasingTransactions(item),
    weightunit: item.weightUnit,
    volumeunit: item.volumeUnit,
    vendorPartNumber: vendorPartNumber,
    unitprice: itemCost,
    class: item.class,
    uom: getBaseUom(item.uoms),
    serials: [],
    taxCode: item.purchaseTaxCode,
    relatedRecords: { item },
    itemDetails: {
      itemWeight: item.weight,
      itemVolume: item.volume,
      itemUoms: item.uoms,
      serialTracking: item.serialTracking,
      lotTracking: item.lotTracking,
      purchaseCost: item.basePurchaseCost,
      type: item.type,
    },
  };
  return newLine;
}

export function createPurchasingLineFromItem(item, emptyLineData, lineNumber) {
  return {
    ...emptyLineData,
    lineNumber,
    item: { id: item.id, name: item.name },
    class: item.class,
    onhand: item.onhand,
    description: descriptionForPurchasingTransactions(item),
    unitprice: item.basePurchaseCost,
    relatedRecords: { item },
    itemDetails: {
      serialTracking: item.serialTracking,
      lotTracking: item.lotTracking,
      itemWeight: item.weight,
      itemVolume: item.volume,
      itemUoms: item.uoms,
      type: item.type,
    },
  };
}

export async function updateReturnToVendorLineWithItem(item, line, record) {
  const { location } = record;
  if (!item) {
    return line;
  }
  // transfer the appropriate properties from the new inventory
  // item to the line item
  const newLine = {
    ...line,
    item,
    description: descriptionForPurchasingTransactions(item),
    weightunit: item.weightUnit,
    volumeunit: item.volumeUnit,
    class: item.class,
    uom: getBaseUom(item.uoms),
    taxCode: item.purchaseTaxCode,
    serials: [],
    relatedRecords: { item },
    itemDetails: {
      itemWeight: item.weight,
      itemVolume: item.volume,
      itemUoms: item.uoms,
      serialTracking: item.serialTracking,
      lotTracking: item.lotTracking,
      purchaseCost: item.basePurchaseCost,
      type: item.type,
    },
  };

  return await setOutboundBinsForLine(location, newLine);
}

export function descriptionForPurchasingTransactions(item) {
  return item.purchaseDescription || item.description;
}
